import { useSearch, HotelAvailabilityErrors, } from '@honeycomb/data';
import { Paper, Stacker, useDisclosure } from '@honeycomb/ui-core';
import React, { useState } from 'react';
import { BetaEstabAction, pushBoardSelection, trackBetaEstabAction } from '@honeycomb/tracking';
import { boardSelectionRouting } from '@honeycomb/routing';
import { HotelSubBoardDialog } from '../HotelBoardOptions/HotelSubBoardDialog';
import { HotelBasketActionType, useHotelBasketContext } from '../HotelBasketProvider';
import { BoardOptionSelector } from './BoardOptionSelector/BoardOptionSelector';
import { RoomOptionGroup } from './RoomOptionGroup/RoomOptionGroup';
import { RoomDetailsDialog } from './RoomDetailsDialog/RoomDetailsDialog';
import { HotelRoomsUnavailable } from './HotelRoomsUnavailable';
import { RoomOptionGroupSkeleton } from './RoomOptionGroup/RoomOptionGroupSkeleton';
import { HotelRoomsHeading } from './HotelRoomsHeading';
import { HotelRoomsError } from './HotelRoomsError';
import { RoomSoldOut } from '../RoomSoldOut/RoomSoldOut';
import { isBoardUpgraded } from '../utils';
export function HotelRoomBoardSelector({ onSearchUnitOpenChange, hotelSummaryResult: roomAvailability, }) {
    var _a, _b, _c, _d, _e, _f, _g;
    const [searchState] = useSearch([]);
    const { tripType } = searchState;
    const { hotelBasket: { selectedRooms, waitingForRooms: waitingForRoomsSelection, availabilityErrors, unavailableRoomIds, estabId, }, dispatch, } = useHotelBasketContext();
    const [subBoardDialogOpen, { open: handleOpenSubBoardDialog, close: handleCloseSubBoardDialog }] = useDisclosure(false);
    const [detailsDialogOpen, { open: handleOpenDetailsDialog, close: handleCloseDetailsDialog }] = useDisclosure(false);
    const waitingForRooms = waitingForRoomsSelection || roomAvailability.loading;
    const [roomDetails, setRoomDetails] = useState();
    const selectedBoardType = (_a = selectedRooms === null || selectedRooms === void 0 ? void 0 : selectedRooms[0]) === null || _a === void 0 ? void 0 : _a.boardTypeDescription;
    const availableBoardOptions = (_d = (_c = (_b = roomAvailability === null || roomAvailability === void 0 ? void 0 : roomAvailability.data) === null || _b === void 0 ? void 0 : _b.hotelRoomsAvailability) === null || _c === void 0 ? void 0 : _c.boardOptions) !== null && _d !== void 0 ? _d : [];
    const activeBoard = availableBoardOptions.find((x) => x.boardTypeDescription === selectedBoardType);
    // Inclusion and Exclusion without empty strings
    const boardInclusion = (activeBoard === null || activeBoard === void 0 ? void 0 : activeBoard.bTInclusions) ? activeBoard === null || activeBoard === void 0 ? void 0 : activeBoard.bTInclusions.filter(Boolean) : [];
    const boardExclusion = (activeBoard === null || activeBoard === void 0 ? void 0 : activeBoard.bTExclusions) ? activeBoard === null || activeBoard === void 0 ? void 0 : activeBoard.bTExclusions.filter(Boolean) : [];
    const hasRoomsAvailable = selectedRooms.length > 0;
    const hasRoomsError = availabilityErrors.includes(HotelAvailabilityErrors.ROOM_ERROR);
    const hasRoomSoldOutError = availabilityErrors.includes(HotelAvailabilityErrors.CONTINUE_TO_BOOK_ERROR);
    const handleBoardChange = (boardTypeDescription) => {
        var _a, _b;
        // Find the selected board option and exit if not found
        const selectedBoard = availableBoardOptions.find((option) => option.boardTypeDescription === boardTypeDescription);
        if (!selectedBoard)
            return;
        // Get the cheapest room from available rooms
        const updatedSelectedRooms = (_a = selectedBoard.requestedRooms) === null || _a === void 0 ? void 0 : _a.flatMap((room) => {
            var _a, _b, _c, _d;
            const possibleRooms = (_a = room.availableRooms) === null || _a === void 0 ? void 0 : _a.filter((r) => !unavailableRoomIds.includes(r.roomId));
            const cheapestRoom = (possibleRooms === null || possibleRooms === void 0 ? void 0 : possibleRooms.length)
                ? possibleRooms.reduce((prev, curr) => {
                    var _a, _b, _c, _d, _e, _f;
                    const prevPrice = (_c = (_b = (_a = prev === null || prev === void 0 ? void 0 : prev.pricingSummary) === null || _a === void 0 ? void 0 : _a.teaserPrice) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : 0;
                    const currPrice = (_f = (_e = (_d = curr === null || curr === void 0 ? void 0 : curr.pricingSummary) === null || _d === void 0 ? void 0 : _d.teaserPrice) === null || _e === void 0 ? void 0 : _e.value) !== null && _f !== void 0 ? _f : 0;
                    return currPrice < prevPrice ? curr : prev;
                })
                : null;
            // return early with no cheapest room, in case of no rooms at all.
            if (!cheapestRoom)
                return [];
            return {
                boardRoomId: cheapestRoom.roomId,
                boardType: (_b = cheapestRoom.pricingSummary) === null || _b === void 0 ? void 0 : _b.boardType,
                boardTypeDescription: (_c = cheapestRoom.pricingSummary) === null || _c === void 0 ? void 0 : _c.boardTypeDescription,
                pricingSummary: cheapestRoom.pricingSummary,
                roomDescription: (_d = cheapestRoom.masterRoom) === null || _d === void 0 ? void 0 : _d.title,
            };
        });
        // Dispatch updated rooms only if there are any
        if (updatedSelectedRooms && updatedSelectedRooms.length) {
            // Tracking update
            pushBoardSelection(estabId, updatedSelectedRooms[0].boardTypeDescription, isBoardUpgraded((_b = roomAvailability === null || roomAvailability === void 0 ? void 0 : roomAvailability.data) === null || _b === void 0 ? void 0 : _b.hotelRoomsAvailability, updatedSelectedRooms[0].boardType));
            dispatch({
                type: HotelBasketActionType.SET_SELECTED_ROOM,
                payload: { selectedRooms: updatedSelectedRooms },
            });
            boardSelectionRouting(updatedSelectedRooms.map((room) => room.boardRoomId).join(','));
        }
    };
    const handleRoomChange = (availableRooms, roomId, roomIndexToUpdate) => {
        var _a;
        // Find room details of the new room and exit early if not found
        const selectedRoomDetails = availableRooms.find((room) => room.roomId === roomId);
        if (!selectedRoomDetails)
            return;
        // Destructure needed properties for clarity
        const { roomId: boardRoomId, pricingSummary, masterRoom } = selectedRoomDetails;
        const { boardType, boardTypeDescription } = pricingSummary;
        // Update the selected room at the given index immutably
        const updatedSelectedRooms = [
            ...selectedRooms.slice(0, roomIndexToUpdate),
            {
                boardRoomId,
                boardType,
                boardTypeDescription,
                pricingSummary,
                roomDescription: masterRoom === null || masterRoom === void 0 ? void 0 : masterRoom.title,
            },
            ...selectedRooms.slice(roomIndexToUpdate + 1),
        ];
        // Tracking update
        pushBoardSelection(estabId, updatedSelectedRooms[0].boardTypeDescription, isBoardUpgraded((_a = roomAvailability === null || roomAvailability === void 0 ? void 0 : roomAvailability.data) === null || _a === void 0 ? void 0 : _a.hotelRoomsAvailability, updatedSelectedRooms[0].boardType));
        // Dispatch the updated selected rooms
        dispatch({
            type: HotelBasketActionType.SET_SELECTED_ROOM,
            payload: { selectedRooms: updatedSelectedRooms },
        });
        boardSelectionRouting(updatedSelectedRooms.map((room) => room.boardRoomId).join(','));
    };
    const handleDetailsClick = React.useCallback((room) => {
        setRoomDetails(room);
        handleOpenDetailsDialog();
    }, [handleOpenDetailsDialog]);
    // Rooms error
    if (hasRoomsError) {
        return (React.createElement(Paper, { py: 5, defaultGutters: true },
            React.createElement(Stacker, { spacing: 6 },
                React.createElement(HotelRoomsHeading, null),
                React.createElement(HotelRoomsError, null))));
    }
    // No rooms available
    if (!waitingForRooms && !hasRoomsAvailable) {
        trackBetaEstabAction(BetaEstabAction.AVAILABILITY_NO_ROOMS, tripType !== null && tripType !== void 0 ? tripType : 'Unknown');
        return (React.createElement(Paper, { py: 5, defaultGutters: true },
            React.createElement(Stacker, { spacing: 6 },
                React.createElement(HotelRoomsHeading, null),
                React.createElement(HotelRoomsUnavailable, null))));
    }
    if (!waitingForRooms &&
        hasRoomsAvailable &&
        ((_f = (_e = roomAvailability === null || roomAvailability === void 0 ? void 0 : roomAvailability.data) === null || _e === void 0 ? void 0 : _e.hotelRoomsAvailability) === null || _f === void 0 ? void 0 : _f.boardOptions.length) === 0) {
        trackBetaEstabAction(BetaEstabAction.ROOMS_MISSING_AVAILABILITY, tripType !== null && tripType !== void 0 ? tripType : 'Unknown');
    }
    return (React.createElement(Paper, { py: 5, defaultGutters: true },
        React.createElement(Stacker, { spacing: 6 },
            React.createElement(HotelRoomsHeading, null),
            hasRoomSoldOutError && React.createElement(RoomSoldOut, { showFullSearch: onSearchUnitOpenChange }),
            React.createElement(BoardOptionSelector, { boardOptions: availableBoardOptions, selectedBoardType: selectedBoardType, onBoardChange: handleBoardChange, waiting: waitingForRooms }),
            waitingForRooms && React.createElement(RoomOptionGroupSkeleton, { numberOfRooms: searchState.rooms.length }),
            !waitingForRooms && hasRoomsAvailable && (React.createElement(React.Fragment, null,
                React.createElement(Stacker, { spacing: 12 }, (_g = activeBoard === null || activeBoard === void 0 ? void 0 : activeBoard.requestedRooms) === null || _g === void 0 ? void 0 : _g.map((room, roomIndex) => {
                    const hasInclusionDetails = (boardInclusion === null || boardInclusion === void 0 ? void 0 : boardInclusion.length) > 0 || (boardExclusion === null || boardExclusion === void 0 ? void 0 : boardExclusion.length) > 0;
                    return (React.createElement(RoomOptionGroup, { key: `room-card-group-${roomIndex.toString()}`, hasInclusionDetails: hasInclusionDetails, room: room, roomIndex: roomIndex, onRoomChange: handleRoomChange, onSubBoardDetailsClick: handleOpenSubBoardDialog, onDetailsClick: handleDetailsClick, selectedRoom: selectedRooms[roomIndex] }));
                })),
                React.createElement(HotelSubBoardDialog, { open: subBoardDialogOpen, exclusions: activeBoard === null || activeBoard === void 0 ? void 0 : activeBoard.bTExclusions, inclusions: activeBoard === null || activeBoard === void 0 ? void 0 : activeBoard.bTInclusions, onClose: handleCloseSubBoardDialog, fullScreen: "mobile-only" }),
                React.createElement(RoomDetailsDialog, { open: detailsDialogOpen, room: roomDetails, onClose: handleCloseDetailsDialog, fullScreen: "mobile-only" }))))));
}
